/**
 * Grid pathfinding for route suggestion.
 *
 * Pure and map-free so the algorithm is testable on hand-built grids. The chart
 * side (src/chart/autoRoute.ts) is responsible for deciding which cells are
 * navigable; this module only finds a way across them.
 *
 * A* on an 8-connected grid. Not because it is clever, but because it is
 * *inspectable*: every cell it crosses was explicitly marked navigable, so a
 * suggested route cannot pass through water the caller never approved. That
 * matters here more than efficiency — the documented failure of competing apps
 * is auto-routing that quietly crosses land.
 */

export interface Grid {
  width: number;
  height: number;
  /** Row-major, `true` where the boat may go. */
  navigable: Uint8Array;
}

export interface Cell {
  x: number;
  y: number;
}

export function createGrid(width: number, height: number): Grid {
  return { width, height, navigable: new Uint8Array(width * height) };
}

export const indexOf = (grid: Grid, x: number, y: number): number => y * grid.width + x;

export const isInside = (grid: Grid, x: number, y: number): boolean =>
  x >= 0 && y >= 0 && x < grid.width && y < grid.height;

export const isNavigable = (grid: Grid, x: number, y: number): boolean =>
  isInside(grid, x, y) && grid.navigable[indexOf(grid, x, y)] === 1;

export function setNavigable(grid: Grid, x: number, y: number, value: boolean): void {
  if (isInside(grid, x, y)) grid.navigable[indexOf(grid, x, y)] = value ? 1 : 0;
}

/** Straight-line cost between neighbours; diagonals cost √2. */
const STRAIGHT = 1;
const DIAGONAL = Math.SQRT2;

const NEIGHBOURS: Array<{ dx: number; dy: number; cost: number }> = [
  { dx: 1, dy: 0, cost: STRAIGHT },
  { dx: -1, dy: 0, cost: STRAIGHT },
  { dx: 0, dy: 1, cost: STRAIGHT },
  { dx: 0, dy: -1, cost: STRAIGHT },
  { dx: 1, dy: 1, cost: DIAGONAL },
  { dx: 1, dy: -1, cost: DIAGONAL },
  { dx: -1, dy: 1, cost: DIAGONAL },
  { dx: -1, dy: -1, cost: DIAGONAL },
];

/** Octile distance — the exact cost of an unobstructed 8-connected path. */
export function octileHeuristic(a: Cell, b: Cell): number {
  const dx = Math.abs(a.x - b.x);
  const dy = Math.abs(a.y - b.y);
  return STRAIGHT * (dx + dy) + (DIAGONAL - 2 * STRAIGHT) * Math.min(dx, dy);
}

export interface FindPathOptions {
  /**
   * Extra cost per cell adjacent to unnavigable water, encouraging the path to
   * stand off the shore rather than shave it. A route that is technically clear
   * but hugs a rock ledge is not one a skipper would choose.
   */
  clearancePenalty?: number;
  /** Abort once this many cells have been expanded. */
  maxExpansions?: number;
}

export interface PathResult {
  /** Cells from start to goal inclusive, or null when no route exists. */
  path: Cell[] | null;
  /** Cells expanded — useful for reporting why a search gave up. */
  expansions: number;
  /** True when the search hit `maxExpansions` before reaching the goal. */
  exhausted: boolean;
}

/**
 * Count of 8-neighbours that are not navigable. Used for the clearance penalty
 * and as a cheap proxy for "how close is the shore".
 */
export function obstructedNeighbours(grid: Grid, x: number, y: number): number {
  let count = 0;
  for (const { dx, dy } of NEIGHBOURS) {
    if (!isNavigable(grid, x + dx, y + dy)) count += 1;
  }
  return count;
}

/**
 * A* from `start` to `goal`.
 *
 * Returns `path: null` when the goal cannot be reached, which is a legitimate
 * and common answer — a harbour behind a causeway has no route. The caller must
 * present that as "no route found", never fall back to a straight line.
 */
export function findPath(
  grid: Grid,
  start: Cell,
  goal: Cell,
  options: FindPathOptions = {},
): PathResult {
  const { clearancePenalty = 0, maxExpansions = 400_000 } = options;

  if (!isNavigable(grid, start.x, start.y) || !isNavigable(grid, goal.x, goal.y)) {
    return { path: null, expansions: 0, exhausted: false };
  }

  const size = grid.width * grid.height;
  const gScore = new Float64Array(size).fill(Infinity);
  const fScore = new Float64Array(size).fill(Infinity);
  const cameFrom = new Int32Array(size).fill(-1);
  const closed = new Uint8Array(size);

  const startIndex = indexOf(grid, start.x, start.y);
  const goalIndex = indexOf(grid, goal.x, goal.y);
  gScore[startIndex] = 0;
  fScore[startIndex] = octileHeuristic(start, goal);

  // A binary heap keyed on fScore. A sorted array would be simpler but turns
  // the search quadratic on the grids a harbour actually needs.
  const heap: number[] = [startIndex];
  const heapPush = (index: number): void => {
    heap.push(index);
    let i = heap.length - 1;
    while (i > 0) {
      const parent = (i - 1) >> 1;
      if (fScore[heap[parent]] <= fScore[heap[i]]) break;
      [heap[parent], heap[i]] = [heap[i], heap[parent]];
      i = parent;
    }
  };
  const heapPop = (): number => {
    const top = heap[0];
    const last = heap.pop()!;
    if (heap.length > 0) {
      heap[0] = last;
      let i = 0;
      for (;;) {
        const left = 2 * i + 1;
        const right = left + 1;
        let smallest = i;
        if (left < heap.length && fScore[heap[left]] < fScore[heap[smallest]]) smallest = left;
        if (right < heap.length && fScore[heap[right]] < fScore[heap[smallest]]) smallest = right;
        if (smallest === i) break;
        [heap[smallest], heap[i]] = [heap[i], heap[smallest]];
        i = smallest;
      }
    }
    return top;
  };

  let expansions = 0;

  while (heap.length > 0) {
    const current = heapPop();
    if (closed[current]) continue;
    closed[current] = 1;
    expansions += 1;

    if (current === goalIndex) {
      const path: Cell[] = [];
      for (let node = current; node !== -1; node = cameFrom[node]) {
        path.push({ x: node % grid.width, y: Math.floor(node / grid.width) });
      }
      return { path: path.reverse(), expansions, exhausted: false };
    }

    if (expansions >= maxExpansions) {
      return { path: null, expansions, exhausted: true };
    }

    const cx = current % grid.width;
    const cy = Math.floor(current / grid.width);

    for (const { dx, dy, cost } of NEIGHBOURS) {
      const nx = cx + dx;
      const ny = cy + dy;
      if (!isNavigable(grid, nx, ny)) continue;

      // Refuse to cut a diagonal corner between two obstructions: the boat
      // would pass through the gap between two rocks that touch diagonally.
      if (dx !== 0 && dy !== 0
        && (!isNavigable(grid, cx + dx, cy) || !isNavigable(grid, cx, cy + dy))) {
        continue;
      }

      const neighbour = indexOf(grid, nx, ny);
      if (closed[neighbour]) continue;

      const penalty = clearancePenalty > 0
        ? clearancePenalty * obstructedNeighbours(grid, nx, ny)
        : 0;
      const tentative = gScore[current] + cost + penalty;
      if (tentative >= gScore[neighbour]) continue;

      cameFrom[neighbour] = current;
      gScore[neighbour] = tentative;
      fScore[neighbour] = tentative + octileHeuristic({ x: nx, y: ny }, goal);
      heapPush(neighbour);
    }
  }

  return { path: null, expansions, exhausted: false };
}

/**
 * Reduce a grid path to the fewest cells that describe the same track.
 *
 * A* returns one cell per step, which would make a route of hundreds of
 * waypoints. Collinear runs collapse to their endpoints, so a straight leg
 * becomes two waypoints regardless of length.
 */
export function simplifyPath(path: readonly Cell[]): Cell[] {
  if (path.length <= 2) return [...path];

  const simplified: Cell[] = [path[0]];
  for (let i = 1; i < path.length - 1; i++) {
    const previous = path[i - 1];
    const current = path[i];
    const next = path[i + 1];
    const turned = (current.x - previous.x) !== (next.x - current.x)
      || (current.y - previous.y) !== (next.y - current.y);
    if (turned) simplified.push(current);
  }
  simplified.push(path[path.length - 1]);
  return simplified;
}

/**
 * Drop waypoints that can be reached directly, by testing whether the straight
 * line between two cells stays navigable.
 *
 * This is what turns a staircase of diagonal steps into the handful of legs a
 * skipper would actually steer. Uses a supercover line so a leg cannot clip the
 * corner of an obstructed cell.
 */
export function straightenPath(grid: Grid, path: readonly Cell[]): Cell[] {
  if (path.length <= 2) return [...path];

  const result: Cell[] = [path[0]];
  let anchor = 0;

  while (anchor < path.length - 1) {
    let furthest = anchor + 1;
    for (let candidate = path.length - 1; candidate > anchor; candidate--) {
      if (hasClearLine(grid, path[anchor], path[candidate])) {
        furthest = candidate;
        break;
      }
    }
    result.push(path[furthest]);
    anchor = furthest;
  }

  return result;
}

/**
 * True when every cell touched by the straight line from `a` to `b` is
 * navigable. Samples densely enough that no cell on the line is skipped.
 */
export function hasClearLine(grid: Grid, a: Cell, b: Cell): boolean {
  const dx = b.x - a.x;
  const dy = b.y - a.y;
  const steps = Math.max(Math.abs(dx), Math.abs(dy)) * 2;
  if (steps === 0) return isNavigable(grid, a.x, a.y);

  for (let i = 0; i <= steps; i++) {
    const t = i / steps;
    const x = Math.round(a.x + dx * t);
    const y = Math.round(a.y + dy * t);
    if (!isNavigable(grid, x, y)) return false;
  }
  return true;
}
